import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import ContentHeaderSection from "shared/common/ContentHeaderSection";
import RightDrawer from "shared/common/RightDrawer";
import apiCall from "shared/services/ApiAdapter";
import useStudentEnrollmentData from "shared/hooks/useStudentEnrollmentData";
import { getCurrentUserId } from "shared/utils/SessionDataUtils";
import { useMasterList } from "shared/hooks/useMasterList";
import masterApiCall from "shared/services/MasterDataApi";

const AssignEvaluationToBatchForm = ({
  navigateTo,
  selectedBatchId,
  selectedCourseTitle,
  selectedEvaluationId,
  useMockData,
  displayToast,
}) => {
  const [availableEvaluations, setAvailableEvaluations] = useState([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState(
    selectedEvaluationId || ""
  );
  const [evaluationDetails, setEvaluationDetails] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(selectedBatchId || "");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignmentDetails, setAssignmentDetails] = useState({
    sentDateTime: new Date().toISOString(),
    expiryDateTime: "",
    isActive: true,
  });

  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const userId = getCurrentUserId();
  const { masterData } = useMasterList({
    useMockData,
    displayToast,
    startLoading,
    stopLoading,
    apiCall: masterApiCall,
  });

  const courseId = evaluationDetails?.course?.id;
  const {
    course,
    batches,
    students,
    enrollments,
    loading: hookLoading,
    error: hookError,
  } = useStudentEnrollmentData({ courseId: courseId || null });

  const batchDetails = useMemo(() => {
    // FIX: Convert the batch ID to a string to match the selectedBatch state.
    return batches.find((b) => b.id?.toString() === selectedBatch);
  }, [selectedBatch, batches]);

  const batchStudents = useMemo(() => {
    if (
      !selectedBatch ||
      !enrollments ||
      !students ||
      enrollments.length === 0 ||
      students.length === 0
    ) {
      return [];
    }
    const enrolledStudentIds = new Set(
      enrollments
        .filter((e) => e.batch?.id?.toString() === selectedBatch)
        .map((e) => e.student?.id)
    );
    return students.filter((s) => enrolledStudentIds.has(s.id));
  }, [selectedBatch, enrollments, students]);

  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        const evaluations = await apiCall(
          `/evaluations?createdBy.equals=${userId}&isActive.equals=true&size=10000`,
          useMockData,
          "GET",
          null,
          startLoading,
          stopLoading,
          displayToast
        );
        setAvailableEvaluations(evaluations || []);
        if (selectedEvaluationId) {
          const preselectedEval = evaluations.find(
            (e) => e.id === selectedEvaluationId
          );
          if (preselectedEval) {
            setEvaluationDetails(preselectedEval);
          }
        }
      } catch (error) {
        console.error("Failed to fetch evaluations:", error);
        displayToast("Failed to fetch evaluations", "error");
      }
    };
    fetchStaticData();
  }, [
    selectedEvaluationId,
    useMockData,
    startLoading,
    stopLoading,
    displayToast,
  ]);

  useEffect(() => {
    if (
      selectedBatchId &&
      batches.length > 0 &&
      enrollments.length > 0 &&
      !selectedBatch
    ) {
      setSelectedBatch(selectedBatchId);
      setIsDrawerOpen(true);
    }
  }, [selectedBatchId, batches, enrollments, selectedBatch]);

  // New useEffect to handle populating expiry date when course data is available
  useEffect(() => {
    if (course?.endDate) {
      // Set expiry date to the course's end date
      const expiryDate = new Date(evaluationDetails.expiryDateTime);
      expiryDate.setHours(23, 59, 59, 999); // Set time to end of day
      setAssignmentDetails((prev) => ({
        ...prev,
        expiryDateTime: expiryDate.toISOString(),
      }));
    }
  }, [course]);

  const handleBatchChange = (e) => {
    const batchId = e.target.value;
    setSelectedBatch(batchId);
    if (batchId) {
      setIsDrawerOpen(true);
    } else {
      setIsDrawerOpen(false);
    }
  };

  const handleEvaluationChange = (e) => {
    const evaluationId = e.target.value;
    setSelectedEvaluation(evaluationId);
    const selected = availableEvaluations.find((ev) => ev.id === evaluationId);
    setEvaluationDetails(selected || null);
  };

  const handleAssignmentDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "datetime-local") {
      const isoString = new Date(value).toISOString();
      setAssignmentDetails((prev) => ({
        ...prev,
        [name]: isoString,
      }));
    } else {
      setAssignmentDetails((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !selectedBatch ||
      !selectedEvaluation ||
      !assignmentDetails.expiryDateTime
    ) {
      displayToast("Please fill all required fields", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const evaluation = availableEvaluations.find(
        (e) => e.id === selectedEvaluation
      );
      // Derive codes for chapter, source, and class
      let chapterCode = evaluation?.chapterCode || "";
      if (!chapterCode && evaluation?.chapter) {
        const match = (masterData?.chapters || []).find(
          (c) => c.label === evaluation.chapter || c.name === evaluation.chapter
        );
        if (match) chapterCode = match.value;
      }
      const classCode =
        evaluation?.classCode || evaluation?.course?.academicClass || "";
      const sourceCode = evaluation?.sourceCode || evaluation?.source || "";

      const assignmentPayload = {
        evaluation: {
          id: evaluation?.id,
          chapterCode,
          classCode,
          sourceCode,
        },
        sentDateTime: assignmentDetails.sentDateTime,
        expiryDateTime: assignmentDetails.expiryDateTime,
        isActive: assignmentDetails.isActive,
      };
      await apiCall(
        `/assignments/batch/${selectedBatch}`,
        useMockData,
        "POST",
        assignmentPayload,
        startLoading,
        stopLoading,
        displayToast
      );
      displayToast(
        `Successfully assigned ${evaluation?.chapter} to ${batchDetails?.batchName}`,
        "success"
      );
      if (selectedBatch || selectedBatchId) {
        const batchToNavigate = selectedBatch || selectedBatchId;
        try {
          sessionStorage.setItem(
            "app_selected_batch_id",
            String(batchToNavigate)
          );
          if (evaluation?.course?.id != null) {
            sessionStorage.setItem(
              "app_selected_course_id",
              String(evaluation.course.id)
            );
          }
        } catch (e) {}
        navigateTo("batchEvaluations", batchToNavigate, selectedCourseTitle);
      } else {
        navigateTo("evaluations");
      }
    } catch (error) {
      displayToast("Failed to assign evaluation to batch", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const rightSideContentTop = (
    <>
      <div className="relative w-full max-w-40">
        <input
          type="text"
          placeholder={t("search")}
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 text-gray-900"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
          🔍
        </span>
      </div>
      <div className="flex items-center justify-center w-10 h-10 text-xl text-gray-100 bg-gray-800 rounded-full cursor-pointer dark:bg-gray-600 dark:text-gray-200">
        ⚙️
      </div>
    </>
  );

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (selectedBatch || selectedBatchId) {
                  const batchToNavigate = selectedBatch || selectedBatchId;
                  navigateTo(
                    "batchEvaluations",
                    batchToNavigate,
                    selectedCourseTitle
                  );
                } else {
                  navigateTo("evaluations");
                }
              }}
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
              aria-label="Go back"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>
            <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
              {t("assignEvaluationToBatch")}
            </span>
          </div>
        }
        subtitle={t("assignEvaluationToBatchSubtitle")}
        rightContentTop={rightSideContentTop}
        rightContentBottom={null}
      />
      <div className="flex flex-col md:flex-row gap-8">
        <div
          className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
          style={{ flexBasis: isDrawerOpen ? "50%" : "calc(100% - 2rem)" }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Evaluation Details Section - Moved up */}
            {(selectedEvaluation || selectedEvaluationId) && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  {t("evaluationDetails")}
                </h3>
                {evaluationDetails ? (
                  <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                    <p>
                      <strong>{t("chapter")}:</strong>{" "}
                      {evaluationDetails.chapter}
                    </p>
                    <p>
                      <strong>{t("topic")}:</strong>{" "}
                      {evaluationDetails.conceptTopic || "N/A"}
                    </p>
                    <p>
                      <strong>{t("difficulty")}:</strong>{" "}
                      {evaluationDetails.difficulty}
                    </p>
                    <p>
                      <strong>{t("questions")}:</strong>{" "}
                      {evaluationDetails.numberOfQuestions}
                    </p>
                    <p>
                      <strong>{t("timeLimit")}:</strong>{" "}
                      {evaluationDetails.maxTimeMinutes} {t("minutes")}
                    </p>
                    <p>
                      <strong>{t("type")}:</strong>{" "}
                      {evaluationDetails.isPractice
                        ? t("practice")
                        : t("assessment")}
                    </p>
                  </div>
                ) : (
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    <p>Loading evaluation details...</p>
                  </div>
                )}
              </div>
            )}

            {course && (
              <div className="mb-4">
                <span className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t("currentCourse")}
                </span>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {course.courseName}
                </h2>
              </div>
            )}
            <div>
              <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">
                {t("selectBatch")}
              </label>
              <select
                value={selectedBatch}
                onChange={handleBatchChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">{t("selectBatch")}</option>
                {batches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.batchName}
                  </option>
                ))}
              </select>
            </div>
            {availableEvaluations.length > 0 && (
              <div>
                <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">
                  {t("selectEvaluation")}
                </label>
                <select
                  value={selectedEvaluation}
                  onChange={handleEvaluationChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">{t("selectEvaluation")}</option>
                  {availableEvaluations.map((evaluation) => (
                    <option key={evaluation.id} value={evaluation.id}>
                      {evaluation.name} -{" "}
                      {evaluation.conceptTopic || "No topic"} (
                      {evaluation.difficulty})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(selectedEvaluation || selectedEvaluationId) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t("assignmentDetails")}
                </h3>
                <div>
                  <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">
                    {t("sentDateTime")}
                  </label>
                  <input
                    type="datetime-local"
                    name="sentDateTime"
                    value={assignmentDetails.sentDateTime.slice(0, 16)}
                    onChange={handleAssignmentDetailsChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">
                    {t("expiryDateTime")}
                  </label>
                  <input
                    type="datetime-local"
                    name="expiryDateTime"
                    value={assignmentDetails.expiryDateTime.slice(0, 16)}
                    onChange={handleAssignmentDetailsChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={assignmentDetails.isActive}
                    onChange={handleAssignmentDetailsChange}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label
                    htmlFor="isActive"
                    className="ml-2 block text-sm text-gray-900 dark:text-gray-300"
                  >
                    {t("active")}
                  </label>
                </div>
              </div>
            )}
            {(selectedEvaluation || selectedEvaluationId) &&
              batchStudents.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                    {t("confirmation")}
                  </h4>
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    {t("assignmentConfirmation")}
                  </p>
                </div>
              )}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  !selectedBatch ||
                  (!selectedEvaluation && !selectedEvaluationId) ||
                  batchStudents.length === 0
                }
                className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? t("assigning") : t("assignToBatch")}
              </button>
            </div>
          </form>
        </div>
        {selectedBatch && (
          <RightDrawer
            title="batchDetails"
            count={batchStudents.length}
            isOpen={isDrawerOpen}
            toggleDrawer={() => setIsDrawerOpen(!isDrawerOpen)}
          >
            {/* Batch Details Section - Moved up in the drawer content */}
            {hookLoading ? (
              <div className="text-center text-gray-600 dark:text-gray-400 py-4">
                Loading batch details...
              </div>
            ) : batchDetails ? (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {t("batchDetails")}
                </h3>
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>{t("batchName")}:</strong> {batchDetails.batchName}
                  </p>
                  <p>
                    <strong>{t("course")}:</strong>{" "}
                    {selectedCourseTitle || "Unknown"}
                  </p>
                  <p>
                    <strong>{t("capacity")}:</strong> {batchStudents.length}/
                    {batchDetails.maxCapacity}
                  </p>
                  <p>
                    <strong>{t("location")}:</strong> {batchDetails.location}
                  </p>
                  <p>
                    <strong>{t("status")}:</strong> {batchDetails.status}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-600 dark:text-gray-400 py-4">
                No batch details available.
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                {t("enrolledStudents")} ({batchStudents.length})
              </h3>
              <ul className="space-y-3">
                {hookLoading ? (
                  <div className="text-center text-gray-600 dark:text-gray-400 py-4">
                    Loading students...
                  </div>
                ) : batchStudents.length === 0 ? (
                  <div className="text-center text-gray-600 dark:text-gray-400 py-4">
                    {t("noStudentsInBatch")}
                  </div>
                ) : (
                  batchStudents.map((student) => (
                    <li
                      key={student.id}
                      className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <img
                        src={
                          student.profilePictureUrl ||
                          `https://placehold.co/40x40/e0f2f7/00796b?text=${student.name.charAt(
                            0
                          )}`
                        }
                        alt={student.name}
                        className="w-10 h-10 rounded-full"
                      />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {student.name}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {student.academicClass} | {student.curriculum}
                        </p>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </RightDrawer>
        )}
      </div>
    </div>
  );
};

export default AssignEvaluationToBatchForm;
